// src/components/LeaveInfoMaster.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import './LeaveInfoMaster.css';

/** 会計開始月から12ヶ月分の月番号を並べる（有給休暇管理画面と同じ並び） */
const getFiscalMonths = (startMonth) =>
  Array.from({ length: 12 }, (_, i) => ((Number(startMonth) - 1 + i) % 12) + 1);

const emptyMonthly = (months) =>
  Object.fromEntries(months.map(m => [String(m), 0]));

const calcTotal = (monthly, months) =>
  months.reduce((s, m) => s + (parseInt(monthly?.[String(m)], 10) || 0), 0);

const num = (v) => parseInt(v, 10) || 0;

// 表示する消化日数 ＝ 保存値（CSV取込・この画面での手入力） ＋ 取得日が過ぎた承認済み申請。
// 編集欄にバインドするのは保存値のみ。合算値を保存し直すと申請分が二重計上されるため、
// 申請由来の分（monthlyUsedFromApplications）はサーバから別に受け取り、表示のときだけ足す。
// 有給休暇管理画面もサーバ側で同じ合算をしているので、2画面の数字は一致する。
const mergeMonthly = (stored, fromApplications, months) => {
  const merged = emptyMonthly(months);
  months.forEach(m => {
    const k = String(m);
    merged[k] = num(stored?.[k]) + num(fromApplications?.[k]);
  });
  return merged;
};

const getDefaultFiscalYear = () => {
  const today = new Date();
  return today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
};

const calcTenure = (startDateStr) => {
  if (!startDateStr) return '-';
  const startDate = new Date(startDateStr);
  if (isNaN(startDate.getTime())) return '-';

  const today = new Date();
  let years = today.getFullYear() - startDate.getFullYear();
  let months = today.getMonth() - startDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }
  return `${years}年${months}ヶ月`;
}

const LeaveInfoMaster = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(getDefaultFiscalYear);
  // 会計開始月（有給休暇管理画面と同じく画面から変更できる）。月別列の並びと集計期間の両方に効く。
  const [fiscalStartMonth, setFiscalStartMonth] = useState(4);
  const months = useMemo(() => getFiscalMonths(fiscalStartMonth), [fiscalStartMonth]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [employees, setEmployees] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [newRecord, setNewRecord] = useState({
    employee_id: '',
    fiscalYear: getDefaultFiscalYear(),
    prevYearEntitled: 0,
    currYearEntitled: 0,
    prevYearUsed: 0,
    monthlyUsed: emptyMonthly(months),
  });

  const fiscalYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 5; y <= currentYear + 2; y++) {
      years.push(y);
    }
    return years.sort((a, b) => b - a);
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/leave/master/', {
        params: { fiscal_year: fiscalYear, fiscal_start_month: fiscalStartMonth },
      });
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, fiscalStartMonth]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    api.get('/employees/', { params: { page_size: 1000 } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        setEmployees(list);
      })
      .catch(() => {});
  }, []);


  const employeeMap = useMemo(() => {
    return new Map(employees.map(emp => [String(emp.memberId), emp.startDate || '']));
  }, [employees]);

  const startEdit = (rec) => {
    setEditingId(rec.id);
    setEditData({
      prevYearEntitled: rec.prevYearEntitled ?? 0,
      currYearEntitled: rec.currYearEntitled ?? 0,
      prevYearUsed: rec.prevYearUsed ?? 0,
      monthlyUsed: { ...emptyMonthly(months), ...(rec.monthlyUsed || {}) },
      fiscalYear: rec.fiscalYear ?? fiscalYear,
    });
  };

  const handleSave = async (id) => {
    try {
      await api.patch(`/admin/leave/master/${id}/`, editData);
      setEditingId(null);
      fetchRecords();
    } catch {
      alert('保存に失敗しました。');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このレコードを削除しますか？')) return;
    try {
      await api.delete(`/admin/leave/master/${id}/`);
      fetchRecords();
    } catch {
      alert('削除に失敗しました。');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setCsvUploading(true);
    setCsvResult(null);
    try {
      // Content-Type は指定しない（axios/ブラウザが boundary 付きで自動設定する）
      const res = await api.post('/admin/leave/master/import-csv/', formData);
      setCsvResult({ success: true, message: `${res.data.updated}件を更新しました`, errors: res.data.errors });
      fetchRecords();
    } catch (err) {
      setCsvResult({ success: false, message: err?.response?.data?.error || 'インポートに失敗しました' });
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleCsvExport = () => {
    const url = `/api/admin/leave/master/export-csv/?fiscal_year=${fiscalYear}`;
    const token = localStorage.getItem('access') ?? sessionStorage.getItem('access') ?? '';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `leave_master_${fiscalYear}.csv`;
        a.click();
      })
      .catch(() => alert('CSVダウンロードに失敗しました'));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/leave/master/', {
        ...newRecord,
        fiscalYear: fiscalYear,
      });
      setShowAddForm(false);
      setNewRecord({
        employee_id: '',
        fiscalYear: fiscalYear,
        prevYearEntitled: 0,
        currYearEntitled: 0,
        prevYearUsed: 0,
        monthlyUsed: emptyMonthly(months),
      });
      fetchRecords();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : '追加に失敗しました。';
      alert(msg);
    }
  };

  const setMonthVal = (month, value, isNew = false) => {
    const m = String(month);
    if (isNew) {
      setNewRecord(prev => ({
        ...prev,
        monthlyUsed: { ...prev.monthlyUsed, [m]: parseInt(value, 10) || 0 },
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        monthlyUsed: { ...prev.monthlyUsed, [m]: parseInt(value, 10) || 0 },
      }));
    }
  };

  return (
    <div className="lm-container">
      <header className="lm-header">
        <h1 className="lm-title">有給休暇マスター</h1>
        <p className="lm-note">
          消化日数は「CSV取込・手入力の実績」＋「取得日が過ぎた承認済みの有給申請」の合計です。
          取得日が来ていない有給は、承認済みでもまだ消化に数えません。
          編集欄で直せるのは手入力分だけで、申請から自動計上された分は <span className="lm-from-app">+N</span> と表示されます。
        </p>
        <div className="lm-header-actions">
          <label className="lm-label">会計年度</label>
          <select
            className="lm-select"
            value={fiscalYear}
            onChange={e => setFiscalYear(Number(e.target.value))}
          >
            {fiscalYearOptions.map(y => (
              <option key={y} value={y}>{y}年度</option>
            ))}
          </select>
          <label className="lm-label">会計開始月</label>
          <select
            className="lm-select"
            value={fiscalStartMonth}
            onChange={e => setFiscalStartMonth(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
              <option key={m} value={m}>{m}月始まり</option>
            ))}
          </select>
          <button className="lm-btn-add" onClick={() => setShowAddForm(v => !v)}>
            {showAddForm ? 'キャンセル' : '＋ 新規追加'}
          </button>
          <label className="lm-btn-csv-upload" title="CSVインポート（1行目:年度,2026 / 2行目:社員コード,前年付与,本年付与,前年消化,4月〜3月）">
            {csvUploading ? 'アップロード中...' : 'CSVアップロード'}
            <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} disabled={csvUploading} />
          </label>
          <button className="lm-btn-csv-export" onClick={handleCsvExport} title="現在の年度データをCSVでダウンロード">
            CSVダウンロード
          </button>
          <button className="lm-btn-back" onClick={() => navigate(-1)}>戻る</button>
        </div>
      </header>

      {/* 新規追加フォーム */}
      {showAddForm && (
        <form className="lm-add-form" onSubmit={handleAdd}>
          <h3 className="lm-add-title">新規レコード追加</h3>
          <div className="lm-form-row">
            <label>社員</label>
            <select
              className="lm-select"
              value={newRecord.employee_id}
              onChange={e => setNewRecord(prev => ({ ...prev, employee_id: e.target.value }))}
              required
            >
              <option value="">-- 社員を選択 --</option>
              {employees.map(emp => (
                <option key={emp.memberId} value={emp.memberId}>{emp.memberId} {emp.name}</option>
              ))}
            </select>
            <label>前年付与</label>
            <input type="number" min="0" className="lm-num-input" value={newRecord.prevYearEntitled}
              onChange={e => setNewRecord(prev => ({ ...prev, prevYearEntitled: e.target.value }))} />
            <label>本年付与</label>
            <input type="number" min="0" className="lm-num-input" value={newRecord.currYearEntitled}
              onChange={e => setNewRecord(prev => ({ ...prev, currYearEntitled: e.target.value }))} />
            <label>前年消化</label>
            <input type="number" min="0" className="lm-num-input" value={newRecord.prevYearUsed}
              onChange={e => setNewRecord(prev => ({ ...prev, prevYearUsed: e.target.value }))} />
          </div>
          <div className="lm-form-row lm-monthly-row">
            {months.map(m => (
              <div key={m} className="lm-month-field">
                <label>{m}月</label>
                <input type="number" min="0" className="lm-num-input"
                  value={newRecord.monthlyUsed?.[String(m)] ?? 0}
                  onChange={e => setMonthVal(m, e.target.value, true)} />
              </div>
            ))}
          </div>
          <button type="submit" className="lm-btn-save">追加</button>
        </form>
      )}

      {csvResult && (
        <div className={`lm-csv-result ${csvResult.success ? 'lm-csv-ok' : 'lm-csv-err'}`}>
          {csvResult.message}
          {csvResult.errors && csvResult.errors.length > 0 && (
            <ul className="lm-csv-errors">
              {csvResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          <button className="lm-csv-close" onClick={() => setCsvResult(null)}>×</button>
        </div>
      )}

      {loading && <div className="lm-status">読み込み中...</div>}
      {error && <div className="lm-status lm-error">{error}</div>}

      {!loading && !error && (
        <div className="lm-table-wrapper">
          <table className="lm-table">
            <thead>
              <tr>
                <th>社員コード</th>
                <th>氏名</th>
                <th>入社年月日</th>
                <th>在籍期間</th>
                <th>前年付与</th>
                <th>本年付与</th>
                <th>前年消化</th>
                {months.map(m => <th key={m}>{m}月</th>)}
                <th>消化計</th>
                <th>残日数</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr><td colSpan={21} className="lm-empty">データがありません。</td>
                  <button 
                      type="button" 
                      className="btn-go-to-admin"
                      onClick={() => navigate('/admin/leave')} // 有給休暇管理を遷移
                      style={{ backgroundColor: '#007bff', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                      「有給休暇管理」画面へ移動して付与計算を実行する
                  </button>
                </tr>
              )}
              {records.map(rec => {
                const isEditing = editingId === rec.id;
                // 承認済み申請から算出された分（編集不可。サーバが保存値と分けて返す）
                const appMonthly  = rec.monthlyUsedFromApplications || {};
                const appPrevUsed = num(rec.prevYearUsedFromApplications);
                // 保存値。編集中は編集中の入力値を使う
                const storedMonthly  = isEditing ? editData.monthlyUsed : (rec.monthlyUsed || {});
                const storedPrevUsed = isEditing ? editData.prevYearUsed : rec.prevYearUsed;
                const prevEntitled   = num(isEditing ? editData.prevYearEntitled : rec.prevYearEntitled);
                const currEntitled   = num(isEditing ? editData.currYearEntitled : rec.currYearEntitled);

                const monthly     = mergeMonthly(storedMonthly, appMonthly, months);
                const total       = calcTotal(monthly, months);
                const prevUsed    = num(storedPrevUsed) + appPrevUsed;
                const remain      = prevEntitled + currEntitled - prevUsed - total;


                const startDate = employeeMap.get(String(rec.memberId)) || rec.employee?.startDate || rec.startDate || '';
                const tenure = calcTenure(startDate);
                
                return (
                  <tr key={rec.id} className={isEditing ? 'lm-row-editing' : ''}>
                    <td>{rec.memberId}</td>
                    <td>{rec.name}</td>
                    <td style={{ color: '#475569', textAlign: 'center' }}>{startDate || '—'}</td>
                    <td style={{ fontWeight: '600', color: '#0f172a', textAlign: 'center' }}>{tenure}</td>
                    <td>
                      {isEditing
                        ? <input type="number" min="0" className="lm-num-input" value={editData.prevYearEntitled}
                            onChange={e => setEditData(p => ({ ...p, prevYearEntitled: e.target.value }))} />
                        : rec.prevYearEntitled ?? 0}
                    </td>
                    <td>
                      {isEditing
                        ? <input type="number" min="0" className="lm-num-input" value={editData.currYearEntitled}
                            onChange={e => setEditData(p => ({ ...p, currYearEntitled: e.target.value }))} />
                        : rec.currYearEntitled ?? 0}
                    </td>
                    <td>
                      {isEditing ? (
                        <>
                          <input type="number" min="0" className="lm-num-input" value={editData.prevYearUsed}
                            onChange={e => setEditData(p => ({ ...p, prevYearUsed: e.target.value }))} />
                          {appPrevUsed > 0 && (
                            <span className="lm-from-app" title="承認済みの有給申請から自動計上（編集できません）">
                              +{appPrevUsed}
                            </span>
                          )}
                        </>
                      ) : prevUsed}
                    </td>
                    {months.map(m => {
                      const k = String(m);
                      const fromApp = num(appMonthly[k]);
                      return (
                        <td key={m}>
                          {isEditing ? (
                            <>
                              <input type="number" min="0" className="lm-num-input lm-month-input"
                                value={editData.monthlyUsed?.[k] ?? 0}
                                onChange={e => setMonthVal(m, e.target.value)} />
                              {fromApp > 0 && (
                                <span className="lm-from-app" title="承認済みの有給申請から自動計上（編集できません）">
                                  +{fromApp}
                                </span>
                              )}
                            </>
                          ) : (monthly[k] ?? 0)}
                        </td>
                      );
                    })}
                    <td className="lm-total">{total}</td>
                    <td className={`lm-remain ${remain <= 0 ? 'lm-remain-zero' : remain <= 10 ? 'lm-remain-low' : ''}`}>
                      {remain}
                    </td>
                    <td className="lm-actions">
                      {isEditing ? (
                        <>
                          <button className="lm-btn-save" onClick={() => handleSave(rec.id)}>保存</button>
                          <button className="lm-btn-cancel" onClick={() => setEditingId(null)}>取消</button>
                        </>
                      ) : (
                        <>
                          <button className="lm-btn-edit" onClick={() => startEdit(rec)}>編集</button>
                          <button className="lm-btn-delete" onClick={() => handleDelete(rec.id)}>削除</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeaveInfoMaster;